import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    timeout: 30000,  // 30s default — long enough for slow analytics, short enough to fail fast on hangs
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('leadhub_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle 401 errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('leadhub_token');
            localStorage.removeItem('leadhub_user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth APIs
export const authAPI = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    register: (data) => api.post('/auth/register', data),
    getMe: () => api.get('/auth/me')
};

// User APIs
export const userAPI = {
    getAll: () => api.get('/users'),
    getAgents: () => api.get('/users/agents'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { new_password: newPassword })
};

// Lead APIs
export const leadAPI = {
    getAll: (params = {}) => api.get('/leads', { params }),
    getPaginated: (params = {}) => api.get('/leads', { params }),
    getById: (id) => api.get(`/leads/${id}`),
    create: (data) => api.post('/leads', data),
    update: (id, data) => api.put(`/leads/${id}`, data),
    assign: (leadId, agentId) => api.post(`/leads/${leadId}/assign?agent_id=${agentId}`),
    assignCounsellor: (leadId, counsellorId) => api.post(`/leads/${leadId}/assign-counsellor`, { counsellor_id: counsellorId }),
    getCounsellors: () => api.get('/leads/counsellors/list'),
    bulkAssign: (leadIds, agentId) => api.post('/leads/bulk-assign', { lead_ids: leadIds, agent_id: agentId }),
    delete: (id) => api.delete(`/leads/${id}`),
    import: (formData) => api.post('/leads/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    addNote: (id, note) => api.post(`/leads/${id}/note`, { note }),
    updateNote: (leadId, noteIndex, note) => api.put(`/leads/${leadId}/notes/${noteIndex}`, { note }),
    deleteNote: (leadId, noteIndex) => api.delete(`/leads/${leadId}/notes/${noteIndex}`),
    updateStatus: (id, status, note) => api.post(`/leads/${id}/status`, { status, note }),
    updateTemperature: (id, temperature) => api.put(`/leads/${id}/temperature`, { temperature }),
    // Payment/Billing APIs
    collectPayment: (leadId, paymentData) => api.post(`/leads/${leadId}/payments`, paymentData),
    getPayments: (leadId) => api.get(`/leads/${leadId}/payments`),
    getPendingBilling: (params = {}) => api.get('/leads/billing/pending', { params }),
    getCompletedBilling: (params = {}) => api.get('/leads/billing/completed', { params })
};

// Call APIs
export const callAPI = {
    getAll: (params = {}) => api.get('/calls', { params }),
    getByLead: (leadId) => api.get(`/calls/lead/${leadId}`),
    create: (data) => api.post('/calls', data),
    simulateCall: (leadId) => api.post(`/calls/simulate`, { lead_id: leadId })
};

// Reminder APIs
export const reminderAPI = {
    getAll: () => api.get('/reminders'),
    create: (data) => api.post('/reminders', data),
    complete: (id) => api.put(`/reminders/${id}/complete`),
    delete: (id) => api.delete(`/reminders/${id}`)
};

// Notification APIs
export const notificationAPI = {
    getAll: () => api.get('/notifications'),
    markRead: (ids) => api.post('/notifications/mark-read', ids)
};

// Campaign APIs
export const campaignAPI = {
    getAll: () => api.get('/campaigns'),
    getById: (id) => api.get(`/campaigns/${id}`),
    create: (data) => api.post('/campaigns', data),
    update: (id, data) => api.put(`/campaigns/${id}`, data),
    delete: (id) => api.delete(`/campaigns/${id}`),
    getAnalytics: (id) => api.get(`/campaigns/${id}/analytics`),
    getSubmissions: (id) => api.get(`/campaigns/${id}/submissions`)
};

// Settings APIs
export const settingsAPI = {
    get: () => api.get('/settings'),
    update: (data) => api.put('/settings', data)
};

// Public Form APIs (no auth required)
export const publicFormAPI = {
    getForm: (campaignId) => axios.get(`${API_BASE}/public/form/${campaignId}`),
    submitForm: (campaignId, data) => axios.post(`${API_BASE}/public/form/${campaignId}/submit`, data)
};

// Dashboard APIs
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getAgentPerformance: (params = {}) => api.get('/dashboard/agent-performance', { params }),
    getCallStatsByDate: (days = 7) => api.get(`/dashboard/call-stats-by-date?days=${days}`),
    getLeadSources: () => api.get('/dashboard/lead-sources'),
    getStatusDistribution: () => api.get('/dashboard/status-distribution'),
    getLeadsByCampaign: () => api.get('/dashboard/leads-by-campaign'),
    getStatusByCampaign: () => api.get('/dashboard/status-by-campaign'),
    getLeadsByAgent: () => api.get('/dashboard/leads-by-agent'),
    getUntouchedLeadsAging: () => api.get('/dashboard/untouched-leads-aging'),
    getCounsellorStats: () => api.get('/dashboard/counsellor-stats'),
    getCampaignAnalytics: () => api.get('/dashboard/campaign-analytics'),
    getApplicationAnalytics: () => api.get('/dashboard/application-analytics'),
    getVisitorAnalytics: () => api.get('/dashboard/visitor-analytics'),
    getManagementDashboard: () => api.get('/dashboard/management-dashboard'),
    getLeadsAnalyticsExtended: () => api.get('/dashboard/leads-analytics-extended'),
    getAgentAnalysis: (params = {}) => api.get('/dashboard/agent-analysis', { params })
};

// Seed API
export const seedAPI = {
    seed: () => api.post('/seed')
};

// Approval APIs
export const approvalAPI = {
    getAll: (params = {}) => api.get('/approvals', { params }),
    getPendingCount: () => api.get('/approvals/pending/count'),
    getById: (id) => api.get(`/approvals/${id}`),
    create: (data) => api.post('/approvals', data),
    processAction: (id, action, remark) => api.post(`/approvals/${id}/action`, { action, remark })
};

export default api;
